import dayjs, { Dayjs } from 'dayjs';
import { FormikProvider, useFormik } from 'formik';
import { useCallback } from 'react';
import * as Yup from 'yup';
import ClientsSelectPaginated from '@/modules/admin-dashboard/components/ClientsSelectPaginated.tsx';
import UsersSelectPaginated from '@/modules/admin-dashboard/components/UsersSelectPaginated.tsx';
import { useQuickCreateTaskMutation } from '@/modules/admin-dashboard/queries/quick-task.ts';
import {
  BaseModalProps,
  Button,
  CalendarInput,
  ModalContentWrapper,
  SelectOption,
  SelectPaginateOption,
  TextInputField,
} from 'ui';
import { getDisableTimeBefore, getDisabledDateBefore } from 'ui/utils';
import { CreateQuickTaskRequest } from 'compliance-api';

export const validationSchema = Yup.object().shape({
  name: Yup.string().min(3).max(100).required('Task name is required'),
  start_date: Yup.date()
    .required('Start date is required')
    .when('first_due_date', ([first_due_date], schema) => {
      return first_due_date
        ? schema.max(first_due_date, 'Start date cannot be after the first due date')
        : schema;
    }),
  first_due_date: Yup.date().required('Due date is required'),
  form_id: Yup.number().min(1, 'Form is required'),
  user_ids: Yup.array().min(1, 'At least one user is required').of(Yup.number()),
  description: Yup.string().max(3000),
});

interface CreateQuickTaskModalProps extends BaseModalProps {
  formId: number;
}

const CreateQuickTaskModal = ({ formId, open, onClose }: CreateQuickTaskModalProps) => {
  const { mutateAsync, isLoading } = useQuickCreateTaskMutation();

  const formik = useFormik<
    CreateQuickTaskRequest & {
      users: SelectOption<number>[];
      client?: SelectPaginateOption<number>;
    }
  >({
    initialValues: {
      name: '',
      start_date: new Date().toISOString(),
      first_due_date: dayjs().add(1, 'month').hour(21).minute(0).second(0).toISOString(),
      form_id: formId,
      users: [],
      description: '',
      client_id: undefined,
      client: undefined,
    },
    validationSchema,
    validateOnBlur: false,
    validateOnChange: false,
    onSubmit: async values => {
      await mutateAsync(values);
      onClose();
    },
  });

  const handleChangeUsers = useCallback(
    async (users: SelectOption<number>[]) => {
      await formik.setFieldValue('users', users);
      await formik.setFieldValue(
        'user_ids',
        users.map(({ value }) => value),
      );
      formik.setFieldError('user_ids', '');
      formik.setFieldError('users', '');

      setTimeout(() => formik.validateField('user_ids'), 1);
    },
    [formik],
  );

  const handleChangeStartDate = useCallback(
    (date: Dayjs) => {
      formik.setFieldValue('start_date', date.toISOString());
      formik.validateField('start_date');
    },
    [formik],
  );

  const handleChangeDueDate = useCallback(
    (date: Dayjs) => {
      formik.setFieldValue('first_due_date', date.toISOString());
      formik.validateField('first_due_date');
    },
    [formik],
  );

  const handleSelectClient = useCallback(
    (option: SelectPaginateOption<number> | null) => {
      formik.setFieldValue('client', option);
      formik.setFieldValue('client_id', option?.value || undefined);
    },
    [formik],
  );

  return (
    <ModalContentWrapper title="Create quick task" open={open} onClose={onClose}>
      <FormikProvider value={formik}>
        <div className="space-y-3">
          <TextInputField label="Name" name="name" placeholder="Name..." />
          <div className="flex gap-2">
            <CalendarInput
              showTime
              label="Start date"
              allowClear={false}
              format="DD-MM-YYYY, HH:mm"
              value={dayjs(formik.values.start_date)}
              disabledTime={getDisableTimeBefore}
              disabledDate={getDisabledDateBefore}
              error={formik.errors.start_date}
              onChange={handleChangeStartDate}
            />
            <CalendarInput
              showTime
              label="Due date"
              allowClear={false}
              format="DD-MM-YYYY, HH:mm"
              value={dayjs(formik.values.first_due_date)}
              disabledTime={getDisableTimeBefore}
              disabledDate={getDisabledDateBefore}
              error={formik.errors.first_due_date}
              onChange={handleChangeDueDate}
            />
          </div>
          <UsersSelectPaginated
            enableCreation
            value={formik.values.users}
            label="Assigned users"
            isMulti={true}
            onChange={handleChangeUsers}
            error={formik.errors.user_ids}
          />
          <ClientsSelectPaginated
            isMulti={false}
            value={formik.values.client}
            onChange={handleSelectClient}
            isClearable={true}
          />
          <TextInputField
            label="Description"
            name="description"
            placeholder="Description..."
            textarea
            limit={3000}
          />
          <Button onClick={formik.submitForm} loading={isLoading} disabled={isLoading}>
            Send task
          </Button>
        </div>
      </FormikProvider>
    </ModalContentWrapper>
  );
};

export default CreateQuickTaskModal;
